import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthMe } from '../../hooks/queries';
import { useProductPreferences } from '../../hooks/useProductPreferences';
import {
  formatPinnedProductLabel,
  formatPinnedProductTooltip,
} from '../../utils/product-preferences';
import { canAccessMonitoring, canAccessSupportNavigation } from '../../utils/support-user';

import {
  FaClipboardList,
  FaChartBar,
  FaServer,
  FaUserShield,
  FaUsers,
  FaTag,
  FaLayerGroup,
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaSignOutAlt,
} from 'react-icons/fa';

import { IconType } from 'react-icons';

const Icon: React.FC<{ component: IconType; [key: string]: any }> = ({ component: Component, ...props }) => {
  return React.createElement(Component as React.ComponentType<any>, props);
};

interface NavSection {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: NavItem[];
}

interface NavItem {
  label: string;
  path: string;
}

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

// CFA Institute geometric mark — extracted from cfainstitute.org/themes/custom/cfa_base/logo.svg
// ViewBox cropped to the mark portion only (0 0 215 211); wordmark rendered separately as HTML.
function CFALogo({ size = 32 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 215 211" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      <path fill="#4476ff" d="M135.14,2.86c-3.36-1-6.8-1.83-10.28-2.49l-.96-.18-13.08,95.89,13.57-8.12.05-.45L135.89,3.08l-.75-.23Z"/>
      <path fill="#4476ff" d="M145.42,6.5l-1.11-.46-10.34,76.16,13.46-8.06.08-.44,8.43-62.13-.55-.3c-3.23-1.76-6.55-3.34-9.96-4.76Z"/>
      <path fill="#4476ff" d="M175.04,25.17c-3.1-2.77-6.36-5.34-9.75-7.73l-1.25-.87-7.03,51.82,13.68-8.19.06-.44,4.65-34.26-.37-.33Z"/>
      <path fill="#4476ff" d="M45.64,17.16l-.66.46c-2.89,2.03-5.67,4.21-8.33,6.52l-.74.64,66.26,70.08,2.15-15.73-.31-.33L45.64,17.16Z"/>
      <path fill="#4476ff" d="M64.68,6.66l-.59.25c-3.39,1.45-6.68,3.09-9.88,4.88l-1.05.58,52.67,55.62,2.13-15.62-.3-.32L64.68,6.66Z"/>
      <path fill="#4476ff" d="M111.36,25.03L87.64,0l-.48.08c-4.11.73-8.14,1.67-12.08,2.86l-1.46.44,35.86,37.86,2.17-15.88-.3-.33Z"/>
      <path fill="#4476ff" d="M208.87,85.83c-.65-3.66-1.49-7.27-2.49-10.8l-.34-1.16-65.56,39.33,14.64,5.59.38-.23,53.48-32.09-.11-.64Z"/>
      <path fill="#4476ff" d="M202.81,64.65c-1.33-3.3-2.82-6.52-4.46-9.64l-.46-.87-82.61,49.48,14.77,5.62.38-.24,72.68-43.61-.3-.74Z"/>
      <path fill="#4476ff" d="M210.27,97.5l-.1-1.51-44.61,26.76,14.89,5.66.38-.22,29.54-17.72.02-.51c.08-1.79.13-3.6.13-5.41,0-2.38-.09-4.73-.25-7.05Z"/>
      <path fill="#4476ff" d="M65.39,173.04l.14.43,10.57,32.93.48.13c3.95,1.13,7.99,2.03,12.11,2.7l1.49.23-15.99-49.77-8.8,13.36Z"/>
      <path fill="#4476ff" d="M103.89,114.55l-8.73,13.25.14.42,26.03,81.06.79-.11c3.51-.57,6.95-1.32,10.33-2.22l.95-.26-29.51-92.13Z"/>
      <path fill="#4476ff" d="M80.35,150.31l.13.42,19.17,59.65.64.04c1.64.07,3.27.11,4.94.11,2.04,0,4.06-.06,6.07-.18l1.2-.06-23.49-73.13-8.65,13.16Z"/>
      <path fill="#4476ff" d="M27.22,115.84l-.25.38-18.98,28.88.19.46c1.61,3.85,3.46,7.57,5.48,11.16l.75,1.32,28.66-43.61-15.84,1.41Z"/>
      <path fill="#4476ff" d="M80.88,111.09l-.24.38-46.68,71.05.59.53c2.61,2.38,5.33,4.64,8.17,6.74l.78.58,53.12-80.67-15.73,1.39Z"/>
      <path fill="#4476ff" d="M54.18,113.46l-.24.37-34.35,52.26.38.53c2.15,3,4.45,5.89,6.9,8.64l.79.89,42.11-64.08-15.6,1.38Z"/>
      <path fill="#4476ff" d="M84.96,89.43l-.44.04L.26,97l-.04.81c-.13,2.19-.23,4.42-.23,6.67,0,1.33.04,2.65.09,3.94l.03.99,95.74-8.47-10.9-11.51Z"/>
      <path fill="#4476ff" d="M47.8,50.12l-.44.04-34.26,3.06-.23.44c-1.98,3.63-3.73,7.41-5.29,11.28l-.55,1.4,51.74-4.62-10.98-11.61Z"/>
      <path fill="#4476ff" d="M66.48,69.87l-.44.04-62.01,5.54-.17.61c-.98,3.55-1.77,7.15-2.39,10.84l-.2,1.18,76.01-6.79-10.8-11.42Z"/>
      <path fill="#4476ff" d="M112.19,111.86l4.85,15.12.42.16,79.16,30.03.39-.7c1.72-3.08,3.3-6.25,4.72-9.52l.39-.9-89.93-34.2Z"/>
      <path fill="#4476ff" d="M125.27,152.67l.42.16,58.23,22.09.43-.48c2.41-2.78,4.7-5.69,6.82-8.7l.69-.98-71.4-27.09,4.81,15Z"/>
      <path fill="#4476ff" d="M133.58,178.63l.42.16,32.14,12.19.4-.29c3.36-2.44,6.6-5.04,9.66-7.85l1.11-1.02-48.6-18.44,4.88,15.25Z"/>
    </svg>
  );
}


export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, account, logout } = useAuth();
  const { data: authMeResponse } = useAuthMe();
  const persona = authMeResponse?.data;
  const { pins, unpin } = useProductPreferences();
  const querySku = new URLSearchParams(location.search).get('sku')?.toUpperCase() ?? null;
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === null ? true : saved === 'true';
  });
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const active = new Set<string>();
    if (location.pathname.startsWith('/orders')) { active.add('orders'); }
    setExpandedSections(active);
  }, [location.pathname]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) { next.delete(sectionId); } else { next.add(sectionId); }
      return next;
    });
  };

  const expand = () => {
    setIsCollapsed(false);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, 'false');
  };

  const toggleCollapse = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newCollapsed));
  };

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const isSectionActive = (section: NavSection) => {
    if (section.path) { return isActive(section.path); }
    return section.children?.some(c => isActive(c.path)) ?? false;
  };

  // When collapsed and user clicks an icon: expand sidebar AND navigate
  const handleCollapsedIconClick = (section: NavSection) => {
    expand();
    const dest = section.path ?? section.children?.[0]?.path;
    if (dest) { navigate(dest); }
    if (section.children) {
      setExpandedSections(new Set([section.id]));
    }
  };

  const handleLogout = async () => {
    try { await logout(); } catch (e) { console.error('Logout failed:', e); }
  };

  const effectivelyCollapsed = mobileOpen ? false : isCollapsed;
  const sidebarWidth = effectivelyCollapsed ? 64 : 240;

  useEffect(() => {
    if (isAuthenticated) {
      const mq = window.matchMedia('(max-width: 768px)');
      const update = () => {
        document.documentElement.style.setProperty(
          '--sidebar-width', mq.matches ? '0px' : `${sidebarWidth}px`
        );
      };
      update();
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    } else {
      document.documentElement.style.setProperty('--sidebar-width', '0px');
    }
  }, [sidebarWidth, isAuthenticated]);

  const isAdmin = canAccessMonitoring(persona);
  const showSupportNav = canAccessSupportNavigation(persona);

  const navSections: NavSection[] = [
    {
      id: 'analytics',
      label: 'Product Analytics',
      icon: <Icon component={FaChartBar} />,
      path: '/analytics',
    },
    {
      id: 'orders',
      label: 'Orders',
      icon: <Icon component={FaClipboardList} />,
      path: '/orders',
    },
    ...(showSupportNav
      ? [
          {
            id: 'customers',
            label: 'Customers',
            icon: <Icon component={FaUsers} />,
            path: '/customers',
          },
          {
            id: 'customer-groups',
            label: 'Customer Groups',
            icon: <Icon component={FaLayerGroup} />,
            path: '/customer-groups',
          },
          {
            id: 'promotions',
            label: 'Promotions',
            icon: <Icon component={FaTag} />,
            path: '/promotions',
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            id: 'monitoring',
            label: 'Monitoring',
            icon: <Icon component={FaServer} />,
            path: '/monitoring',
          },
          {
            id: 'bc-permissions-audit',
            label: 'BC Permissions',
            icon: <Icon component={FaUserShield} />,
            path: '/internal/bc-permissions-audit',
          },
        ]
      : []),
  ];

  if (!isAuthenticated) { return null; }

  const pageLabel =
    location.pathname.startsWith('/orders') ? 'Orders' :
    location.pathname.startsWith('/customers') ? 'Customers' :
    location.pathname.startsWith('/promotions') ? 'Promotions' :
    location.pathname.startsWith('/customer-groups') ? 'Customer Groups' :
    location.pathname.startsWith('/analytics') || location.pathname.startsWith('/ambr') ? 'Product Analytics' :
    location.pathname.startsWith('/monitoring') ? 'Monitoring' :
    location.pathname.startsWith('/internal/bc-permissions-audit') ? 'BC Permissions' :
    'CFA Institute';

  // Shared nav item styles
  const navItem = (active: boolean, disabled = false): React.CSSProperties => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: effectivelyCollapsed ? '0.6rem 0' : '0.55rem 0.75rem',
    justifyContent: effectivelyCollapsed ? 'center' : 'flex-start',
    borderRadius: 8,
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    textDecoration: 'none',
    fontSize: '0.85rem',
    fontWeight: active ? 600 : 400,
    color: active ? '#fff' : 'rgba(255,255,255,0.65)',
    background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
    transition: 'background 0.15s, color 0.15s',
    position: 'relative',
  });

  return (
    <>
      {/* Mobile top bar */}
      <div className="mobile-topbar">
        <button className="mobile-hamburger" onClick={() => setMobileOpen(true)} aria-label="Open menu">
          <span /><span /><span />
        </button>
        <span className="mobile-topbar-title">{pageLabel}</span>
        <span style={{ background: '#16a34a', color: '#fff', fontSize: '0.62rem', padding: '0.15rem 0.45rem', borderRadius: 999, fontWeight: 700 }}>PROD</span>
      </div>

      {mobileOpen && <div className="mobile-backdrop" onClick={() => setMobileOpen(false)} />}

      <aside
        className={mobileOpen ? 'sidebar-mobile-open' : ''}
        style={{
          width: `${sidebarWidth}px`,
          background: 'linear-gradient(180deg, #0f172a 0%, #1a2332 100%)',
          color: '#fff',
          height: '100vh',
          position: 'fixed',
          left: 0, top: 0,
          display: 'flex', flexDirection: 'column',
          transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
          zIndex: 1000,
          overflowY: 'auto', overflowX: 'hidden',
          borderRight: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {/* Logo + Brand */}
        <div style={{
          padding: effectivelyCollapsed ? '1.1rem 0' : '1rem 1rem 0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          justifyContent: effectivelyCollapsed ? 'center' : 'flex-start',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          marginBottom: '0.25rem',
        }}>
          <CFALogo size={34} />
          {!effectivelyCollapsed && (
            <div>
              <div style={{ fontSize: '0.88rem', fontWeight: 700, letterSpacing: '-0.2px', lineHeight: 1.2 }}>CFA Institute</div>
              <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.45)', marginTop: 1 }}>Commerce Intelligence</div>
            </div>
          )}
        </div>

        {/* Environment selector */}
        <div style={{ padding: effectivelyCollapsed ? '0.5rem 0.5rem' : '0.5rem 0.75rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '0.5rem 0.625rem', display: 'flex', flexDirection: 'column', gap: '0.15rem', overflowY: 'auto' }}>

          {!effectivelyCollapsed && (
            <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '0.5rem 0.25rem 0.25rem', marginTop: '0.25rem' }}>
              Navigation
            </div>
          )}

          {navSections.map((section) => {
            const isMonitoring = section.id === 'monitoring';
            const showDivider = isMonitoring && !effectivelyCollapsed;
            const isExpanded = expandedSections.has(section.id);
            const active = isSectionActive(section);

            // Collapsed: single click expands sidebar + navigates
            if (effectivelyCollapsed) {
              return (
                <React.Fragment key={section.id}>
                {showDivider && <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '0.4rem 0' }} />}
                <button
                  title={section.label}
                  onClick={() => handleCollapsedIconClick(section)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0.65rem 0', border: 'none', borderRadius: 8, cursor: 'pointer',
                    background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                    color: active ? '#fff' : 'rgba(255,255,255,0.65)',
                    position: 'relative', transition: 'background 0.15s, color 0.15s',
                    fontSize: '1rem',
                  }}
                  onMouseOver={e => { e.currentTarget.style.background = active ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
                  onMouseOut={e => { e.currentTarget.style.background = active ? 'rgba(255,255,255,0.12)' : 'transparent'; e.currentTarget.style.color = active ? '#fff' : 'rgba(255,255,255,0.65)'; }}
                >
                  {active && <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: '60%', background: '#3b82f6', borderRadius: '0 3px 3px 0' }} />}
                  {section.icon}
                </button>
                </React.Fragment>
              );
            }

            // Expanded: single link
            if (section.path && !section.children) {
              return (
                <React.Fragment key={section.id}>
                {showDivider && <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '0.4rem 0' }} />}
                {showDivider && (
                  <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '0.25rem 0.25rem 0.1rem' }}>
                    Admin
                  </div>
                )}
                <Link to={section.path!} style={navItem(active)}
                  onMouseOver={e => { if (!active) { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)'; (e.currentTarget as HTMLElement).style.color = '#fff'; } }}
                  onMouseOut={e => { if (!active) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.65)'; } }}
                >
                  {active && <div style={{ position: 'absolute', left: -10, top: '50%', transform: 'translateY(-50%)', width: 3, height: '60%', background: '#3b82f6', borderRadius: '0 3px 3px 0' }} />}
                  <span style={{ fontSize: '0.95rem', marginRight: '0.65rem', opacity: active ? 1 : 0.7 }}>{section.icon}</span>
                  <span>{section.label}</span>
                </Link>
                {section.id === 'analytics' && pins.length > 0 && (
                  <div
                    data-testid="sidebar-product-pins"
                    style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', margin: '0.15rem 0 0.35rem 0.35rem' }}
                  >
                    {pins.map((pinned) => {
                      const pinActive =
                        location.pathname.startsWith('/analytics') && querySku === pinned.sku;
                      const label = formatPinnedProductLabel(pinned);
                      const tooltip = formatPinnedProductTooltip(pinned);
                      return (
                        <div
                          key={pinned.sku}
                          title={tooltip}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            borderRadius: 8,
                            background: pinActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                          }}
                        >
                          <Link
                            to={`/analytics?sku=${encodeURIComponent(pinned.sku)}`}
                            title={tooltip}
                            aria-label={tooltip}
                            style={{
                              ...navItem(pinActive),
                              flex: 1,
                              padding: '0.4rem 0.65rem',
                              fontSize: '0.78rem',
                            }}
                          >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {label}
                            </span>
                          </Link>
                          <button
                            type="button"
                            aria-label={`Unpin ${tooltip}`}
                            data-testid={`sidebar-unpin-${pinned.sku}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              unpin(pinned.sku);
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: 'rgba(255,255,255,0.45)',
                              cursor: 'pointer',
                              padding: '0.35rem 0.45rem',
                              fontSize: '0.75rem',
                              borderRadius: 6,
                            }}
                            title={`Unpin ${tooltip}`}
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                </React.Fragment>
              );
            }

            // Expanded: expandable section
            return (
              <div key={section.id}>
                <button
                  onClick={() => toggleSection(section.id)}
                  style={{ ...navItem(active), justifyContent: 'space-between' }}
                  onMouseOver={e => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; } }}
                  onMouseOut={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; } }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {active && <div style={{ position: 'absolute', left: -10, top: '50%', transform: 'translateY(-50%)', width: 3, height: '60%', background: '#3b82f6', borderRadius: '0 3px 3px 0' }} />}
                    <span style={{ fontSize: '0.95rem', marginRight: '0.65rem', opacity: active ? 1 : 0.7 }}>{section.icon}</span>
                    <span>{section.label}</span>
                  </div>
                  <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>
                    {isExpanded ? <Icon component={FaChevronUp} /> : <Icon component={FaChevronDown} />}
                  </span>
                </button>
                {isExpanded && section.children && (
                  <div style={{ marginLeft: '1.65rem', borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '0.65rem', marginTop: '0.15rem', display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                    {section.children.map(child => (
                      <Link key={child.path} to={child.path}
                        style={{
                          display: 'block', padding: '0.4rem 0.5rem',
                          color: isActive(child.path) ? '#fff' : 'rgba(255,255,255,0.55)',
                          textDecoration: 'none', fontSize: '0.82rem',
                          fontWeight: isActive(child.path) ? 600 : 400,
                          borderRadius: 6,
                          background: isActive(child.path) ? 'rgba(255,255,255,0.1)' : 'transparent',
                          transition: 'background 0.15s, color 0.15s',
                        }}
                        onMouseOver={e => { if (!isActive(child.path)) { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLElement).style.color = '#fff'; } }}
                        onMouseOut={e => { if (!isActive(child.path)) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.55)'; } }}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Collapse toggle */}
        <div style={{ padding: '0.5rem 0.625rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button
            onClick={toggleCollapse}
            style={{ ...navItem(false), justifyContent: effectivelyCollapsed ? 'center' : 'flex-start' }}
            onMouseOver={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
            aria-label={effectivelyCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon component={FaChevronLeft} style={{ fontSize: '0.8rem', transition: 'transform 0.25s', transform: effectivelyCollapsed ? 'rotate(180deg)' : 'none' }} />
            {!effectivelyCollapsed && <span style={{ marginLeft: '0.6rem', fontSize: '0.8rem' }}>Collapse</span>}
          </button>
        </div>

        {/* User + Sign out */}
        <div style={{ padding: '0.75rem 0.625rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          {!effectivelyCollapsed && account && (
            <div style={{ padding: '0 0.25rem 0.5rem', overflow: 'hidden' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {account.name || account.username || 'User'}
              </div>
              <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
                {account.username}
              </div>
            </div>
          )}
          {effectivelyCollapsed ? (
            <button onClick={handleLogout} title="Sign Out"
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0.6rem 0', border: 'none', borderRadius: 8, cursor: 'pointer',
                background: 'transparent', color: 'rgba(255,255,255,0.65)',
                transition: 'background 0.15s, color 0.15s', fontSize: '0.95rem',
              }}
              onMouseOver={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.color = '#fca5a5'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
            >
              <Icon component={FaSignOutAlt} />
            </button>
          ) : (
            <button onClick={handleLogout}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.5rem 0.75rem', borderRadius: 8, border: 'none',
                background: 'rgba(239,68,68,0.1)', color: 'rgba(252,165,165,0.9)',
                cursor: 'pointer', fontSize: '0.82rem', fontWeight: 500,
                transition: 'background 0.15s',
              }}
              onMouseOver={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
            >
              <Icon component={FaSignOutAlt} style={{ fontSize: '0.85rem' }} />
              <span>Sign Out</span>
            </button>
          )}
        </div>
      </aside>
    </>
  );
}
